import React, { useState } from 'react';
import { usePlans } from 'hooks/plan';
import { Plan, Interval } from 'models/Plan';
import { PlanType } from 'enum/planTypes';
import useLifetimePromo from 'hooks/useLifetimePromo';
import { FaCheck, FaTimes, FaCheckCircle, FaClock } from 'react-icons/fa';
import './SavingsCalculator.scss';

const MAX_MONTHS = 48;
const CHECK = '__check__';
const CROSS = '__cross__';

const CheckIcon = ({ className }: { className?: string }) => <FaCheck className={className} />;
const CrossIcon = () => <FaTimes className="fi fi-cross" />;

const SavingsCalculator: React.FC = () => {
  const { plans } = usePlans();
  const { discountPct, discountedPrice } = useLifetimePromo();
  const [months, setMonths] = useState(12);

  const lifetimePlan = plans?.find((p: Plan) => p.interval === Interval.LIFETIME);
  const premiumPlan  = plans?.find((p: Plan) => p.type === PlanType.PREMIUM && p.interval === Interval.MONTH);

  if (!lifetimePlan || !premiumPlan) return null;

  const originalLifetimePrice = Number(lifetimePlan.price);
  const lifetimePrice   = discountedPrice(originalLifetimePrice);
  const premiumMonthly  = Number(premiumPlan.price);
  const premiumTotal    = premiumMonthly * months;
  const savings         = premiumTotal - lifetimePrice;
  const hasSavings      = savings > 0;
  const breakEvenMonths = Math.ceil(lifetimePrice / premiumMonthly);

  const sliderPct      = Math.min((months / MAX_MONTHS) * 100, 100);
  const premiumBarPct  = Math.min((premiumTotal / (premiumMonthly * MAX_MONTHS)) * 100, 100);
  const lifetimeBarPct = Math.min((lifetimePrice / (premiumMonthly * MAX_MONTHS)) * 100, 100);

  const features = [
    { label: 'Listing Searches / mo',  premium: premiumPlan.listingSearches.toLocaleString(),  lifetime: lifetimePlan.listingSearches.toLocaleString() },
    { label: 'Keyword Searches / mo',  premium: premiumPlan.keywordsSearches.toLocaleString(), lifetime: lifetimePlan.keywordsSearches.toLocaleString() },
    { label: 'Store Searches / mo',    premium: premiumPlan.storeSearches.toLocaleString(),    lifetime: lifetimePlan.storeSearches.toLocaleString() },
    { label: 'Market Searches / mo',   premium: premiumPlan.marketSearches.toLocaleString(),   lifetime: lifetimePlan.marketSearches.toLocaleString() },
    { label: 'Monthly fee',            premium: `Rs. ${premiumMonthly.toLocaleString()}`,      lifetime: 'Never again' },
    { label: 'Renewal required',       premium: 'Every month',                                lifetime: 'Never' },
    { label: 'Unlimited tools',        premium: CROSS,                                        lifetime: CHECK },
  ];

  return (
    <section className="savings-calculator">
      <div className="sc-container">

        {/* Header */}
        <div className="sc-header">
          <h2>Premium vs. Lifetime</h2>
          <p>See exactly how much you save — and when Lifetime pays for itself.</p>
        </div>

        {/* Outer card containing both plan columns */}
        <div className="comparison-card">
          <div className="plans-row">

            {/* Premium column */}
            <div className="plan-col">
              <div className="plan-header premium-header">
                <span className="plan-label">Premium</span>
                <div className="plan-price">
                  Rs. {premiumMonthly.toLocaleString()}
                  <span className="plan-period">/ month</span>
                </div>
                <p className="plan-sub">Billed every month</p>
              </div>
              <ul className="plan-features">
                {features.map(f => (
                  <li key={f.label}>
                    {f.premium === CROSS
                      ? <CrossIcon />
                      : <CheckIcon className="fi fi-check" />}
                    <span className="fi-label">{f.label}</span>
                    {f.premium !== CROSS && f.premium !== CHECK && (
                      <span className="fi-value">{f.premium}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Column divider */}
            <div className="col-divider" />

            {/* Lifetime column */}
            <div className="plan-col lifetime-col">
              <div className="plan-header lifetime-header">
                <span className="best-badge">Best Value</span>
                <span className="plan-label">Lifetime</span>
                {discountPct > 0 && (
                  <div className="plan-price plan-price-original">
                    Rs. {originalLifetimePrice.toLocaleString()}
                  </div>
                )}
                <div className="plan-price">
                  Rs. {lifetimePrice.toLocaleString()}
                  <span className="plan-period">one-time</span>
                </div>
                <p className="plan-sub">
                  {discountPct > 0 ? `${discountPct}% off — pay once, use forever` : 'Pay once, use forever'}
                </p>
              </div>
              <ul className="plan-features lifetime-features">
                {features.map(f => (
                  <li key={f.label}>
                    <CheckIcon className="fi fi-check lifetime-check" />
                    <span className="fi-label">{f.label}</span>
                    {f.lifetime !== CROSS && f.lifetime !== CHECK && (
                      <span className="fi-value lifetime-val">{f.lifetime}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>

        {/* Savings slider card */}
        <div className="slider-card">
          <h3 className="slider-title">
            If you stay for&nbsp;
            <span className="month-pill">{months} month{months !== 1 ? 's' : ''}</span>
          </h3>

          <input
            type="range"
            min={1}
            max={MAX_MONTHS}
            value={months}
            onChange={e => setMonths(Number(e.target.value))}
            className="sc-slider"
            style={{ '--fill': `${sliderPct}%` } as React.CSSProperties}
          />
          <div className="track-labels">
            <span>1 month</span>
            <span>{MAX_MONTHS} months</span>
          </div>

          <div className="cost-bars">
            <div className="bar-row">
              <span className="bar-label">Premium after {months} mo</span>
              <div className="bar-bg"><div className="bar-fill premium-fill" style={{ width: `${premiumBarPct}%` }} /></div>
              <span className="bar-amount">Rs. {premiumTotal.toLocaleString()}</span>
            </div>
            <div className="bar-row">
              <span className="bar-label">Lifetime (fixed)</span>
              <div className="bar-bg"><div className="bar-fill lifetime-fill" style={{ width: `${lifetimeBarPct}%` }} /></div>
              <span className="bar-amount lifetime-amount">Rs. {lifetimePrice.toLocaleString()}</span>
            </div>
          </div>

          <div className={`result-banner ${hasSavings ? 'is-saving' : 'not-saving'}`}>
            {hasSavings ? (
              <>
                <FaCheckCircle className="result-icon saving-icon" />
                <div>
                  <p className="result-headline">You save <strong>Rs. {savings.toLocaleString()}</strong></p>
                  <p className="result-sub">Lifetime paid for itself {months - breakEvenMonths} month{months - breakEvenMonths !== 1 ? 's' : ''} ago</p>
                </div>
              </>
            ) : (
              <>
                <FaClock className="result-icon pending-icon" />
                <div>
                  <p className="result-headline">Break-even in <strong>{breakEvenMonths - months} more month{breakEvenMonths - months !== 1 ? 's' : ''}</strong></p>
                  <p className="result-sub">After month {breakEvenMonths}, every rupee you pay Premium is wasted</p>
                </div>
              </>
            )}
          </div>
        </div>

      </div>
    </section>
  );
};

export default SavingsCalculator;
